import { useState, useContext, useEffect, useMemo } from 'react'
import { PassContext } from '../pass-context'
import { flatten } from "flat"
import _ from "lodash"


import { MenuItem, TextField, Typography, Button, Tooltip } from '@mui/material'
import EastRoundedIcon from '@mui/icons-material/EastRounded'
import DatasetOutlinedIcon from '@mui/icons-material/DatasetOutlined'

import dataSelector from './data-selector'
import dataSelector2 from './data-selector-2'
import VariablesSelector from '/utils/variable-selector'
import PopupShell from '../popup-shell'
import { validateRequiredVariableValue } from '/utils/variable-required'
import currencies from '/utils/currencies.json'

const hasTrimmedValue = value => {
  if (typeof value !== "string") return false
  return value.trim().length > 0
}

const PopupDialogText = ({ open, close, title, sources, path, required, variable }) => {

  const { classData, objectData, variables, setClassData, setObjectData, setGoogleBoth, setVariables, openVariablesManager, variablesCatalog = [], isManagedPassTemplate = false } = useContext(PassContext)

  const [newClassData, setNewClassData] = useState({})
  const [newObjectData, setNewObjectData] = useState({})
  const [newVariables, setNewVariables] = useState({})
  const [sourcePaths, setSourcePaths] = useState([])
  const catalogMap = useMemo(() => _.keyBy(variablesCatalog, "var_name"), [variablesCatalog])

  const dataChanged = useMemo(() => {
    if (open) {
      if (!_.isEqual(objectData, newObjectData)) return true
      if (!_.isEqual(classData, newClassData)) return true
      if (!_.isEqual(variables, newVariables)) return true
    }

    return false
  }, [classData, variables, newClassData, newVariables, newObjectData, objectData, open])

  const templateSource = sources?.[0]

  const editedValue = useMemo(() => {
    return _.get(newObjectData, path)
  }, [newClassData, newObjectData, path, templateSource])

  const showRequiredError = required && !hasTrimmedValue(editedValue)
  const variablesPath = variable ?? (typeof _.get(newObjectData, path) === "string" ? (_.get(newObjectData, path).match(/^{{(.+)}}$/)?.[1] ?? "") : "")
  const instanceRequiredValidation = useMemo(() => validateRequiredVariableValue({
    value: _.get(newVariables, variablesPath),
    catalogEntry: catalogMap[variablesPath],
    label: "Variable value",
    isManagedPassTemplate
  }), [catalogMap, isManagedPassTemplate, variablesPath, newVariables])
 
  // Runs on "open" state change or if template / variables change 
  // Ensures we have the current data rather than something left behind from previous form 
  useEffect(() => {
    try {
      if (open) {
        setNewClassData(_.cloneDeep(classData))
        setNewObjectData(_.cloneDeep(objectData))
        setNewVariables(_.cloneDeep(variables))
      }
    }
    catch (err) {
      throw err
    }
  }, [classData, variables, objectData, open, path, sources])

  // Refresh our template / instance data source paths if instancedata changes
  useEffect(() => {
    setSourcePaths(Object.keys(flatten(newVariables)).map(p => `{{${p}}}`).sort())
  }, [newVariables])

  //////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Support Functions
  //////////////////////////////////////////////////////////////////////////////////////////////////////////

  const saveFormData = event => {
    event.preventDefault() // Prevents a "submit" which reloads the page

    if (showRequiredError) return

    // Save content of our "new" template/variables back to state
    // We need to use googleBoth rather than separate googleClass and googleObject if we are saving both data objects
    if (!_.isEqual(newClassData, classData) && !_.isEqual(newObjectData, objectData)) {
      setGoogleBoth({
        googleClass: newClassData,
        googleObject: newObjectData
      })
    } else {
      if (!_.isEqual(newClassData, classData)) setClassData(newClassData)
      if (!_.isEqual(newObjectData, objectData)) setObjectData(newObjectData)
    }
    if (!_.isEqual(newVariables, variables)) setVariables(newVariables)
    close()
  }

  const handleClose = (e, reason) => {
    if (reason && reason === "backdropClick")
      return
    close()
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Main Render
  //////////////////////////////////////////////////////////////////////////////////////////////////////////

  try {
    return (
      <PopupShell open={open} onClose={handleClose}>

        <div className="flex flex-col h-full gap-2">

          <div className="flex flex-row justify-between mt-5">
            <Typography color="secondary" variant="h6">{title}</Typography>
            <Tooltip title="You can press ESC on your keyboard to dismiss"><Button endIcon={<EastRoundedIcon />} onClick={handleClose} >cancel</Button></Tooltip>
          </div>

          <hr />

          <div className='mt-5 overflow-y-scroll flex flex-col gap-5 mb-[100px] pt-3'>
            <DataSourceInstance path={path} title={title} newObjectData={newObjectData} setNewObjectData={setNewObjectData} variables={newVariables} setNewVariables={setNewVariables} sourcePaths={sourcePaths} required={required} showRequiredError={showRequiredError} catalogMap={catalogMap} isManagedPassTemplate={isManagedPassTemplate} variable={variable} requiredValidation={instanceRequiredValidation} />
          </div>

          <div className='fixed bottom-5 w-[460px]'>
            <hr className='mb-5' />
            <div className="flex flex-row gap-5 justify-between">
              <Button startIcon={<DatasetOutlinedIcon />} onClick={openVariablesManager} >Manage variables</Button>
              <Button variant="contained" disabled={!dataChanged || showRequiredError || !instanceRequiredValidation.valid} onClick={saveFormData}>Save</Button>
            </div>
          </div>

        </div>
      </PopupShell>
    )
  }
  catch (err) {
    throw err
  }
}

export default PopupDialogText


//////////////////////////////////////////////////////////////////////////////////////////////////////////
// Controlled JSX (Must have values passes as paramets and not access them from the parent 
// - hence outside the main function). Otherwise they do not update when data changes!
//////////////////////////////////////////////////////////////////////////////////////////////////////////

const DataSourceInstance = ({ newObjectData, path, setNewObjectData, variables, setNewVariables, sourcePaths, required, showRequiredError, catalogMap, isManagedPassTemplate, variable, requiredValidation }) => {
  try {
    let templateValue = _.get(newObjectData, path)
    const fixedVariableValue = variable ? `{{${variable}}}` : null

    useEffect(() => {
      if (!fixedVariableValue) return
      if (_.get(newObjectData, path) === fixedVariableValue) return

      setNewObjectData(prev => {
        let nd = { ...prev }
        _.set(nd, path, fixedVariableValue)

        if (/defaultValue/.test(path)) {
          const languagePath = path.replace(/value/, "language")
          _.set(nd, languagePath, "en-US")
        }

        return nd
      })
    }, [fixedVariableValue, newObjectData, path, setNewObjectData])

    if (typeof templateValue !== "undefined") return (
      <SimpleSelection active={Boolean(fixedVariableValue)} newObjectData={newObjectData} path={path} variables={variables} setNewObjectData={setNewObjectData} setNewVariables={setNewVariables} sourcePaths={sourcePaths} required={required} showRequiredError={showRequiredError} catalogMap={catalogMap} isManagedPassTemplate={isManagedPassTemplate} variable={variable} requiredValidation={requiredValidation} />
    )
  }
  catch (err) {
    throw err
  }
}

const SimpleSelection = ({ active, path, newObjectData, variables, setNewObjectData, setNewVariables, sourcePaths, required, showRequiredError, catalogMap, isManagedPassTemplate, variable, requiredValidation }) => {

  const templateValue = _.get(newObjectData, path)
  const resolvedValue = dataSelector2({ objectData: newObjectData, variables, path })
  const variablesPath = variable ?? (typeof templateValue === "string" ? (templateValue.match(/^{{(.+)}}$/)?.[1] ?? "") : "")
  const variableValue = variablesPath ? _.get(variables, variablesPath) : ""
  const catalogEntry = catalogMap[variablesPath]
  const varSubType = catalogEntry?.var_type
  const instanceValueReadonly = catalogEntry?.editable !== undefined && Number(catalogEntry?.editable) === 0

  const changeHandler = value => {
    setNewObjectData(prev => {
      let nd = { ...prev }
      _.set(nd, path, value)

      if (/defaultValue/.test(path)) { // This is a localised value. Must add language...
        const languagePath = path.replace(/value/,"language")
        _.set(nd,languagePath,"en-US")
      }

      return nd
    })
  }

  const changeInstanceValue = value => {
    if (!variablesPath || instanceValueReadonly) return

    setNewVariables(prev => {
      let nd = _.cloneDeep(prev)
      _.set(nd, variablesPath, value)
      return nd
    })
  }

  if (active && typeof resolvedValue !== "undefined") return (<>
      {variable
        ? (
          <TextField
            label="Variable name"
            value={variable}
            slotProps={{ inputLabel: { shrink: true } }}
            disabled={true}
            fullWidth
          />
        )
        : <VariablesSelector updateValue={changeHandler} sourcePaths={sourcePaths} value={templateValue} dataSelector={dataSelector} variables={variables} />}

    {varSubType === "choice" ? (
      <TextField
        select
        label="Variable value"
        value={variableValue ?? ""}
        slotProps={{ inputLabel: { shrink: true } }}
        disabled={instanceValueReadonly}
        required={required}
        error={showRequiredError || !requiredValidation.valid}
        helperText={requiredValidation.helperText || undefined}
        fullWidth
        sx={{ mb: 1 }}
        onChange={e => changeInstanceValue(e.target.value)}
      >
        <MenuItem value="">Select currency</MenuItem>
        {currencies.map(currency => (
          <MenuItem key={currency.code} value={currency.code}>
            {currency.code} - {currency.name}
          </MenuItem>
        ))}
      </TextField>
    ) : (
      <TextField label="Variable value"
        value={variableValue ?? ""}
        slotProps={{ inputLabel: { shrink: true } }}
        type="search"
        disabled={instanceValueReadonly}
        required={required}
        error={showRequiredError || !requiredValidation.valid}
        helperText={requiredValidation.helperText || undefined}
        fullWidth
        sx={{ mb: 1 }}
        onChange={e => changeInstanceValue(e.target.value)}
      />
    )}

    <TextField
      label="Resolved value"
      value={resolvedValue?.value ?? ""}
      slotProps={{ inputLabel: { shrink: true } }}
      type="search"
      disabled={true}
      fullWidth
    />
  </>
  )
}
